import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { FileDownload } from '@mui/icons-material';
import { getUsers, getInfluencers, getNegotiationPending, getNegotiationCompleted, getPendingInfluencers, getAssignedInfluencers, getCompletedInfluencers, getNegotiationInProgress } from '../services/api';
import * as XLSX from 'xlsx';

function Report() {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [rawData, setRawData] = useState(null);

  useEffect(() => {
    fetchReportData();
  }, []);

  const filterByDate = (items, dateField = 'createdAt') => {
    if (!fromDate && !toDate) return items;
    return items.filter(item => {
      const itemDate = new Date(item[dateField]);
      if (fromDate && itemDate < new Date(fromDate)) return false;
      if (toDate) {
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);
        if (itemDate > end) return false;
      }
      return true;
    });
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const [usersResponse, pendingResponse, completedResponse, pendingInfluencersResponse, assignedInfluencersResponse, completedInfluencersResponse, negotiationInProgress, allInfluencersResponse] = await Promise.all([
        getUsers(),
        getNegotiationPending(),
        getNegotiationCompleted(),
        getPendingInfluencers(),
        getAssignedInfluencers(),
        getCompletedInfluencers(),
        getNegotiationInProgress(),
        getInfluencers()
      ]);

      const [notInterestedResponse] = await Promise.all([
        import('../services/api').then(mod => mod.default.get('/negotiation/not-interested'))
      ]);

      setRawData({
        users: usersResponse.data.filter(user => user.role === 'onboarder'),
        allInfluencers: allInfluencersResponse.data || [],
        pendingInfluencers: pendingResponse.data.data,
        inProgressInfluencers: negotiationInProgress.data.data,
        completedInfluencers: completedResponse.data.data,
        assignedExistingInfluencers: assignedInfluencersResponse.data?.data || [],
        completedExistingInfluencers: completedInfluencersResponse.data?.data || [],
        notInterestedInfluencers: notInterestedResponse.data?.data || []
      });
    } catch (error) {
      console.error('Failed to fetch report data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!rawData) return;
    const { users, allInfluencers, pendingInfluencers, inProgressInfluencers, completedInfluencers, assignedExistingInfluencers, completedExistingInfluencers, notInterestedInfluencers } = rawData;

    const filteredAll = filterByDate(allInfluencers);
    const filteredPending = filterByDate(pendingInfluencers);
    const filteredInProgress = filterByDate(inProgressInfluencers);
    const filteredCompleted = filterByDate(completedInfluencers);
    const filteredNotInterested = filterByDate(notInterestedInfluencers);
    const filteredAssigned = filterByDate(assignedExistingInfluencers);
    const filteredCompletedExisting = filterByDate(completedExistingInfluencers);

    const reportRows = users.map((user, index) => {
      const userPending = filteredPending.filter(inf => inf.onboarderId?._id === user._id);
      const userNegotiationInProgress = filteredInProgress.filter(inf => inf.onboarderId?._id === user._id);
      const userCompleted = filteredCompleted.filter(inf => inf.onboarderId?._id === user._id);
      const userNotInterested = filteredNotInterested.filter(inf => inf.onboarderId?._id === user._id);
      const userAllInfluencers = filteredAll.filter(inf => inf.onboarderId?._id === user._id && inf.platformType !== 'Barter');

      const userAssigned = filteredAssigned.filter(inf => inf.assignedTo?._id === user._id);
      const userCompletedExisting = filteredCompletedExisting.filter(inf => inf.assignedTo?._id === user._id);

      const instaSavings = userCompleted.reduce((sum, inf) => {
        const reelCost = Number(inf.reelCost) || 0;
        const negotiatedCost = Number(inf.negotiationCostData?.reelCost) || 0;
        if (reelCost === 0) return sum;
        return sum + ((reelCost - negotiatedCost) / reelCost) * 100;
      }, 0);

      const youtubeSavings = userCompleted.reduce((sum, inf) => {
        const originalTotal = (Number(inf.youtubeShortsCost) || 0) + (Number(inf.youtubeVideoCost) || 0);
        const negotiatedTotal = (Number(inf.negotiationCostData?.youtubeShortsCoast) || 0) + (Number(inf.negotiationCostData?.youtubeVideoCost) || 0);
        if (originalTotal === 0) return sum;
        return sum + ((originalTotal - negotiatedTotal) / originalTotal) * 100;
      }, 0);

      return {
        id: user._id,
        sno: index + 1,
        onboarderName: user.name || user.username,
        onboardingFormsSubmitted: userAllInfluencers.length,
        assignedCount: userAssigned.length + userCompletedExisting.length,
        assignedPending: userAssigned.length,
        assignedCompleted: userCompletedExisting.length,
        negotiatedPending: userPending.length,
        negotiatedInProgress: userNegotiationInProgress.length,
        negotiatedNotInterested: userNotInterested.length,
        negotiatedCompleted: userCompleted.length,
        instaSavingsPercent: userCompleted.length > 0 ? Math.round(instaSavings / userCompleted.length).toString() : '0',
        youtubeSavingsPercent: userCompleted.length > 0 ? (youtubeSavings / userCompleted.length).toFixed(2) : '0.00'
      };
    });

    setReportData(reportRows);
  }, [rawData, fromDate, toDate]);

  const getTotalRow = () => {
    const totals = reportData.reduce((acc, row) => ({
      onboardingFormsSubmitted: acc.onboardingFormsSubmitted + row.onboardingFormsSubmitted,
      negotiatedPending: acc.negotiatedPending + row.negotiatedPending,
      negotiatedInProgress: acc.negotiatedInProgress + row.negotiatedInProgress,
      negotiatedNotInterested: acc.negotiatedNotInterested + row.negotiatedNotInterested,
      negotiatedCompleted: acc.negotiatedCompleted + row.negotiatedCompleted,
      instaSavingsSum: acc.instaSavingsSum + parseFloat(row.instaSavingsPercent)
    }), { onboardingFormsSubmitted: 0, negotiatedPending: 0, negotiatedInProgress: 0, negotiatedNotInterested: 0, negotiatedCompleted: 0, instaSavingsSum: 0 });

    return {
      id: 'total',
      onboarderName: 'Total',
      onboardingFormsSubmitted: totals.onboardingFormsSubmitted,
      negotiatedPending: totals.negotiatedPending,
      negotiatedInProgress: totals.negotiatedInProgress,
      negotiatedNotInterested: totals.negotiatedNotInterested,
      negotiatedCompleted: totals.negotiatedCompleted,
      instaSavingsPercent: Math.round(totals.instaSavingsSum).toString()
    };
  };

  const exportToExcel = () => {
    try {
      const data = reportData.map(row => ({
        'SNO': row.sno,
        'Onboarder Name': row.onboarderName,
        'Onboarding Forms Submitted Count': row.onboardingFormsSubmitted,
        // 'Assigned Count': row.assignedCount,
        // 'Assigned - Pending': row.assignedPending,
        // 'Assigned - Completed': row.assignedCompleted,
        'Negotiated - Pending': row.negotiatedPending,
        'Negotiated - In Progress': row.negotiatedInProgress,
        'Negotiated - Not Interested': row.negotiatedNotInterested,
        'Negotiated - Completed': row.negotiatedCompleted,
        'Overall Negotiated Savings %(Insta)': `${row.instaSavingsPercent}%`,
        // 'Overall Negotiated Savings %(YouTube)': `${row.youtubeSavingsPercent}%`
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Report Data');
      XLSX.writeFile(workbook, 'report_data.xlsx');
    } catch (error) {
      console.error('Failed to export data:', error);
    }
  };

  const columns = [
    { field: 'sno', headerName: 'S.No', width: 80, headerTooltip: 'Serial Number' },
    { field: 'onboarderName', headerName: 'Onboarder Name', flex: 1, minWidth: 150, headerTooltip: 'Onboarder Name' },
    { field: 'onboardingFormsSubmitted', headerName: 'Onboarding Forms Submitted Count', flex: 1, minWidth: 200, headerTooltip: 'Onboarding Forms Submitted Count' },
    // { field: 'assignedCount', headerName: 'Assigned Count', flex: 0.8, minWidth: 120, headerTooltip: 'Assigned Count' },
    // { field: 'assignedPending', headerName: 'Assigned - Pending', flex: 0.8, minWidth: 130, headerTooltip: 'Assigned - Pending' },
    // { field: 'assignedCompleted', headerName: 'Assigned - Completed', flex: 0.8, minWidth: 140, headerTooltip: 'Assigned - Completed' },
    { field: 'negotiatedPending', headerName: 'Negotiated - Pending', flex: 0.8, minWidth: 140, headerTooltip: 'Negotiated - Pending' },
    { field: 'negotiatedInProgress', headerName: 'Negotiated - In Progress', flex: 0.8, minWidth: 160, headerTooltip: 'Negotiated - In Progress' },
    { field: 'negotiatedNotInterested', headerName: 'Negotiated - Not Interested', flex: 0.8, minWidth: 180, headerTooltip: 'Negotiated - Not Interested' },
    { field: 'negotiatedCompleted', headerName: 'Negotiated - Completed', flex: 0.8, minWidth: 160, headerTooltip: 'Negotiated - Completed' },
    {
      field: 'instaSavingsPercent',
      headerName: 'Overall Negotiated Savings %(Insta)',
      flex: 1,
      minWidth: 200,
      headerTooltip: 'Overall Negotiated Savings %(Insta)',
      renderCell: (params) => `${params.value}%`
    },
    // { 
    //   field: 'youtubeSavingsPercent', 
    //   headerName: 'Overall Negotiated Savings %(YouTube)', 
    //   flex: 1, 
    //   minWidth: 220,
    //   headerTooltip: 'Overall Negotiated Savings %(YouTube)',
    //   renderCell: (params) => `${params.value}%`
    // }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Report
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            label="From Date"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To Date"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => { setFromDate(''); setToDate(''); }}
            disabled={!fromDate && !toDate}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownload />}
            onClick={exportToExcel}
          >
            Export Excel
          </Button>
        </Box>
      </Box>

      <DataGrid
        columns={columns}
        rows={[...reportData, getTotalRow()]}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        getRowClassName={(params) => params.id === 'total' ? 'total-row' : ''}
        sx={{
          boxShadow: 2,
          backgroundColor: 'white',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f8f9fa'
          },
          '& .MuiDataGrid-iconSeparator': {
            display: 'none'
          },
          '& .MuiDataGrid-row': {
            backgroundColor: 'white',
            borderLeft: 'solid 1px #e5e7eb',
            borderRight: 'solid 1px #e5e7eb'
          },
          '& .total-row': {
            backgroundColor: '#f0f7ff',
            fontWeight: 'bold',
            '& .MuiDataGrid-cell': {
              fontWeight: 'bold'
            }
          }
        }}
      />
    </Box>
  );
}

export default Report;